import {
  Banner,
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { isResponseWithData } from 'lib-shared-auth';
import { DraftCustomAttributeValue } from 'lib-shared-legacy-types';
import {
  formatE164,
  getPhoneNumberCountryCodeFormatted,
  isValidPhoneNumber,
} from 'lib-shared-phone-numbers';
import type {
  CreateConversationFromSmsApiArg,
  CreateConversationWrapupMessageApiArg,
} from 'lib-shared-types-api';
import { useRef, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useMount, useUpdateEffect } from 'react-use';

import { aiApi } from 'apis/ai';
import { conversationsApi } from 'apis/conversations';
import { customFieldsApi, CustomFieldValuePatch } from 'apis/customFields';
import { numbersApi, PhoneNumber } from 'apis/numbers';
import { smsMaxMessageLength } from 'app/utils/conversation';
import { copilotActionContexts } from 'components/copilotActions';
import { CopilotPinnedActions } from 'components/copilotPinnedActions';
import { EditorCopilotDropdown } from 'components/editorCopilotDropdown';
import { useGetComposeModalCopilotActions } from 'components/editorCopilotDropdown/utils/composeModalCopilotActions';
import { EditorMentions } from 'components/editorMentions';
import { EditorQuickRepliesDropdown } from 'components/editorQuickRepliesDropdown';
import {
  HookFormCategorizedTagsSelect,
  HookFormPhoneNumberSelect,
  HookFormQueuesSelect,
  HookFormRichTextField,
  HookFormUserPhoneNumberSelect,
} from 'components/hookForm';
import { PhoneNumberUserSelectValue } from 'components/phoneNumberUserSelect';
import {
  getLocalSettings,
  updateSettings,
} from 'components/settings/api/personal';
// eslint-disable-next-line no-restricted-imports
import { ToggleBox, ToggleBoxBody, ToggleBoxHead } from 'fable/toggleBox';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import { ComposeCustomAttributesToggle } from '../composeCustomAttributes';

import styles from './composeSmsModal.module.scss';

type FieldValues = Omit<
  CreateConversationFromSmsApiArg,
  'from' | 'to' | 'queueId'
> & {
  queueId: string[];
  from: [PhoneNumber];
  to?: PhoneNumberUserSelectValue;
  internalNote?: CreateConversationWrapupMessageApiArg['text'];
  customAttributes?: Record<string, DraftCustomAttributeValue>;
  customAttributesOpen?: boolean;
};

const defaultFromNumberStorageKey = 'composeSmsDefaultFromNumberStorageKey';

const savedDefaultFromNumber: string | undefined =
  getLocalSettings()?.[defaultFromNumberStorageKey];

export const ComposeSmsModal = createAppModal(
  { type: 'app:composeSms', featureFlag: 'sms-compose' },
  ({ isOpen, hide }): JSX.Element => {
    const containerRef = useRef<HTMLDivElement>(null);
    const notify = useNotifications();
    const hasTagAccess = useHasAccess('conversation.tag');
    const [showInternalNote, setShowInternalNote] = useState(false);

    const form = useForm<FieldValues>({ mode: 'all' });

    const message = form.watch('message');
    const internalNote = form.watch('internalNote');

    const [createConversationFromSms, createConversationFromSmsState] =
      conversationsApi.useCreateConversationFromSmsMutation();
    const [createConversationWrapupMessage] =
      conversationsApi.useCreateConversationWrapupMessageMutation();
    const [readNumbers, { isLoading: isReadNumbersLoading }] =
      numbersApi.useLazyReadNumbersQuery();
    const [updateCustomFieldValue] = customFieldsApi.useUpdateValueMutation();

    const { data: styleGuide } = aiApi.useReadAIWritingStyleGuideMatchQuery(
      {
        features: 'impr_reply',
        channels: 'sms',
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

    // Search the API for the number saved in local storage and if found, set it as the initial
    // value in the form when the component mounts.
    useMount(async () => {
      if (!savedDefaultFromNumber) return;

      const response = await readNumbers(
        {
          searchTerm: savedDefaultFromNumber,
        },
        true,
      ).unwrap();

      if (isResponseWithData(response)) {
        const [defaultFromNumber] = response.data.numbers;

        if (defaultFromNumber?.e164_format === savedDefaultFromNumber) {
          form.setValue('from', [defaultFromNumber]);
        }
      }
    });

    const fromValue = useWatch({ control: form.control, name: 'from' });
    const toValue = useWatch({ control: form.control, name: 'to' });

    const handleSubmit: SubmitHandler<FieldValues> = async (
      values,
    ): Promise<void> => {
      const {
        internalNote,
        customAttributes,
        customAttributesOpen,
        ...createSmsValues
      } = values;

      const createSmsResponse = await createConversationFromSms({
        ...createSmsValues,
        to: formatE164(createSmsValues.to?.phoneNumber) ?? '',
        from: formatE164(createSmsValues.from[0].e164_format) ?? '',
        queueId: values.queueId[0],
        tags: values.tags ? values.tags : [],
        message: values.message,
      });

      if (isResponseWithData(createSmsResponse)) {
        const csid = createSmsResponse.data.data.csid;

        if (customAttributes && Object.keys(customAttributes).length > 0) {
          try {
            await updateCustomFieldValue({
              entityId: 'conversation',
              entityInstanceId: String(csid),
              patch: {
                before: {} as CustomFieldValuePatch,
                after: customAttributes as unknown as CustomFieldValuePatch,
              },
            });
          } catch (error) {
            // Log error but don't block - SMS was already sent successfully
            console.error('Failed to save custom attributes:', error);
            notify({
              title: 'Custom attributes not saved',
              text: 'SMS sent successfully, but custom attributes could not be saved.',
              icon: 'i-warning',
            });
          }
        }

        notify(
          {
            title: 'New message sent',
            text: `The sms conversation has been created.`,
            icon: 'i-done',
            time: 5000,
            leftAction: {
              text: 'Go to conversation',
              action: {
                navigate: `/conversation/${csid}`,
              },
            },
          },
          NotificationType.Success,
        );

        if (showInternalNote && internalNote) {
          const createInternalNoteResponse =
            await createConversationWrapupMessage({
              ...values,
              csid,
              text: internalNote,
            });

          if (!isResponseWithData(createInternalNoteResponse)) {
            notify(
              {
                title: 'Internal note not created.',
              },
              NotificationType.Error,
            );
          }
        }
        hide?.();
      } else {
        notify(
          {
            title: 'Error creating sms conversation.',
          },
          NotificationType.Error,
        );
      }
    };

    // Handles triggering validating of the `to` field when `from` field changes, as well as saving
    // the selected `from` number to local storage to be used as the default next time.
    useUpdateEffect(() => {
      if (toValue) {
        form.trigger('to');
      }

      const fromNumber = fromValue[0].e164_format;

      if (fromNumber !== savedDefaultFromNumber) {
        updateSettings({
          key: defaultFromNumberStorageKey,
          value: fromNumber,
        });
      }
    }, [fromValue]);

    const { editorCopilotActions, internalNoteEditorCopilotActions } =
      useGetComposeModalCopilotActions({
        channel: 'sms',
        context: copilotActionContexts.composeSms,
        noteContext: copilotActionContexts.composeSmsNote,
        hasStyleGuideForChannel: !!styleGuide?.data?.match,
      });

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title="Compose new SMS" onClose={hide}></ModalHead>

        <ModalBody>
          <FlexBox flexDirection="column" gap="400">
            <div>
              <Banner
                variant="info"
                description={
                  <Text.Paragraph as="span" variant="4">
                    The origin and destination numbers need to be in the same
                    country code.
                  </Text.Paragraph>
                }
              />
            </div>
            <div ref={containerRef}>
              <FormGrid>
                <FormField>
                  <HookFormPhoneNumberSelect
                    name="from"
                    label="From"
                    control={form.control}
                    rules={{
                      required: 'Phone number is required',
                    }}
                    componentProps={{
                      capability: 'sms',
                      showCountryFlag: true,
                    }}
                    disabled={isReadNumbersLoading}
                  />
                </FormField>
                <FormField>
                  <HookFormUserPhoneNumberSelect
                    name="to"
                    label="To"
                    control={form.control}
                    rules={{
                      validate: {
                        isValidFormat: (value) =>
                          (value && isValidPhoneNumber(value.phoneNumber)) ||
                          `Invalid phone number.`,
                        phoneNumber: (value) =>
                          !fromValue ||
                          value?.phoneNumber?.startsWith(
                            getPhoneNumberCountryCodeFormatted(
                              fromValue[0].e164_format,
                            ) ?? '+0', // fails requirement if it does not find the country code
                          ) ||
                          'The origin and destination numbers must be in the same country',
                      },
                      required: 'Phone number is required',
                    }}
                    componentProps={{
                      infiniteScroll: false,
                      showRegionFlagPrefix: true,
                    }}
                  />
                </FormField>

                <FormField>
                  <HookFormRichTextField
                    name="message"
                    label="Message"
                    control={form.control}
                    rules={{
                      required: 'Message is required',
                      maxLength: {
                        value: smsMaxMessageLength,
                        message: `Reached limit of ${smsMaxMessageLength} characters`,
                      },
                    }}
                    componentProps={{
                      placeholder: 'Type text message...',
                      showCharacterLimit: true,
                      hideSendOnEnterToggle: true,
                      hasBorder: true,
                      channel: 'sms',
                      disableSubmitMessageWithEnter: true,
                      onTriggerSend: form.handleSubmit(handleSubmit),
                      toolbarAddons: (
                        <>
                          <EditorQuickRepliesDropdown
                            popoverContent={{ side: 'bottom', align: 'start' }}
                            enduserId={toValue?.searchUserHit?.id || null}
                          />
                          <EditorCopilotDropdown
                            editorCopilotActions={editorCopilotActions}
                            channel="sms"
                          />
                          <CopilotPinnedActions
                            context={copilotActionContexts.composeSms}
                            channel="sms"
                            text={message}
                          />
                        </>
                      ),
                    }}
                  />
                </FormField>

                <FormField>
                  <ToggleBox
                    className={styles.toggleBox}
                    isOpen={showInternalNote}
                    headComponent={ToggleBoxHead}
                    title={'Internal note'}
                    helpIcon={undefined}
                    input={{
                      id: 'internal-note-toggle-box',
                      name: 'internal-note-toggle-box',
                      value: 'internal-note-toggle-box',
                    }}
                    onChange={(open) => setShowInternalNote(open)}
                  >
                    <ToggleBoxBody>
                      {showInternalNote && (
                        <FormField>
                          <HookFormRichTextField
                            name="internalNote"
                            control={form.control}
                            contentType="html"
                            componentProps={{
                              className: styles.internalNoteEditorColor,
                              placeholder: 'Type an internal note...',
                              showCharacterLimit: false,
                              channel: 'notes',
                              hasBorder: true,
                              hideSendOnEnterToggle: true,
                              disableSubmitMessageWithEnter: true,
                              onTriggerSend: form.handleSubmit(handleSubmit),
                              toolbarAddons: (
                                <>
                                  <EditorMentions container={containerRef} />
                                  <EditorQuickRepliesDropdown
                                    popoverContent={{
                                      side: 'bottom',
                                      align: 'start',
                                    }}
                                    enduserId={
                                      toValue?.searchUserHit?.id || null
                                    }
                                  />
                                  <EditorCopilotDropdown
                                    editorCopilotActions={
                                      internalNoteEditorCopilotActions
                                    }
                                    channel="sms"
                                  />
                                  <CopilotPinnedActions
                                    context={
                                      copilotActionContexts.composeSmsNote
                                    }
                                    channel="sms"
                                    text={internalNote ?? ''}
                                  />
                                </>
                              ),
                            }}
                          />
                        </FormField>
                      )}
                    </ToggleBoxBody>
                  </ToggleBox>
                </FormField>

                <FormField>
                  <ComposeCustomAttributesToggle
                    value={form.watch('customAttributes')}
                    onChange={(values) =>
                      form.setValue('customAttributes', values)
                    }
                    queueId={form.watch('queueId')?.[0]}
                    isOpen={form.watch('customAttributesOpen') ?? false}
                    headComponent={ToggleBoxHead}
                  />
                </FormField>

                <FormField>
                  <Tooltip
                    content="You don't have permissions to add tags"
                    disabled={hasTagAccess}
                  >
                    <HookFormCategorizedTagsSelect
                      control={form.control}
                      disabled={!hasTagAccess}
                      label="Tags"
                      name="tags"
                    />
                  </Tooltip>
                </FormField>

                <FormField>
                  <HookFormQueuesSelect
                    name="queueId"
                    label="Queue"
                    control={form.control}
                    rules={{
                      required: 'Choosing a queue is required',
                    }}
                  />
                </FormField>
              </FormGrid>
            </div>
          </FlexBox>
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName="Send"
          confirmOnClick={form.handleSubmit(handleSubmit)}
          confirmProps={{
            isLoading: createConversationFromSmsState.isLoading,
            loadingIndicator: 'Sending...',
            disabled: !form.formState.isValid || form.formState.isSubmitting,
          }}
        />
      </Modal>
    );
  },
);
